import { Client, cacheExchange, fetchExchange } from '@urql/core';
import { graphql } from './gql';

export class ErrorsIndexer {
  private client: Client;
  public readonly url: string;
  public readonly logger: typeof console;

  constructor(url: string, logger: typeof console = console) {
    this.url = url;
    this.logger = logger;
    this.client = new Client({
      url: url,
      exchanges: [cacheExchange, fetchExchange],
      requestPolicy: 'network-only',
    });
  }

  public async getTransactionsByPeriod({
    workerAddress,
    solutionNamespace,
    rewardPeriod,
  }: {
    workerAddress: string;
    solutionNamespace: string;
    rewardPeriod: number;
  }) {
    try {
      const query = graphql(`
        query GetTransactionsByPeriod(
          $workerAddress: String!
          $rewardPeriod: Int!
          $solutionNamespace: String!
        ) {
          failedVotes(
            where: {
              workerAddress_eq: $workerAddress
              rewardPeriodIndex_eq: $rewardPeriod
              solutionNamespace_eq: $solutionNamespace
            }
          ) {
            solutionNamespace
            rewardPeriodIndex
            extrinsic {
              errorName
              extrinsicHash
            }
            blockNumber
            blockHash
            result
            votingRoundId
          }
        }
      `);

      const result = await this.client.query(query, {
        workerAddress,
        rewardPeriod,
        solutionNamespace,
      });
      const transactions = result?.data?.failedVotes.map((vote) => ({
        blockHash: vote.blockHash,
        blockNumber: vote.blockNumber,
        extrinsicHash: vote?.extrinsic?.extrinsicHash,
        errorName: vote?.extrinsic?.errorName,
        rewardPeriod: vote.rewardPeriodIndex,
        result: vote.result,
        successful: false,
        votingRoundId: vote.votingRoundId,
      }));
      return transactions || [];
    } catch (e) {
      this.logger.error('getFailedVotes', { e });
      return [];
    }
  }

  public async getVotingGraphData({
    workerAddress,
    solutionNamespace,
  }: {
    workerAddress: string;
    solutionNamespace: string;
  }) {
    const query = graphql(`
      query GetVotingGraphData($workerAddress: String!, $solutionNamespace: String!) {
        failedVotes(
          where: { workerAddress_eq: $workerAddress, solutionNamespace_eq: $solutionNamespace }
        ) {
          result
          rewardPeriodIndex
        }
      }
    `);

    const result = await this.client.query(query, { workerAddress, solutionNamespace });
    const parsedResults = result?.data?.failedVotes.map((vote) => ({
      rewardPeriod: vote.rewardPeriodIndex,
      result: vote.result,
      successful: false,
    }));
    return parsedResults || [];
  }
}
